// Service-worker registration. Uses workbox-window (already a dependency).
//
// registerType is 'prompt' (vite.config.ts) — we control activation. On FIRST
// install we claim immediately so the very first navigation already has the
// cached shell + image bucket. On an UPDATE we leave the new SW waiting rather
// than swapping under a possibly-live capture, and surface the update through
// `useAppUpdate` so components/shell/update-banner.tsx can offer it on a screen
// where interrupting is safe (AppShell only renders the banner where the shell
// chrome renders, so a capture/detail view never gets it).
//
// WHY THE DISCOVERY TIMER EXISTS. This half of the file is not decoration: an
// installed PWA is backgrounded, never closed, so it can go days without a page
// load. The browser only re-fetches the SW script on navigation, and an SPA
// does not navigate — so without an explicit `wb.update()` the `waiting` event
// never fires and the update is never even DISCOVERED, let alone activated.
import { Workbox } from 'workbox-window';

import { isPublicPath } from '@/lib/public-paths';

import { useAppUpdate } from './update-store';

/**
 * How often to ask the browser to re-fetch the SW script. Hourly: frequent
 * enough that a deploy reaches a phone that stayed open through a show day,
 * cheap enough to be invisible (one conditional GET of a small file).
 */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

let workbox: Workbox | null = null;
/**
 * Set only by triggerSkipWaitingReload, and the reason the `controlling`
 * listener is safe. `controlling` fires on ANY controller change — including
 * the first install below, where we skip-waiting on purpose and the SW calls
 * clients.claim(). Reloading unconditionally there would bounce every
 * first-time visitor mid-boot.
 */
let reloadOnControlling = false;

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // A buyer opened a shared link. Registering here would install the staff PWA
  // and start precaching the whole catalogue onto a stranger's phone.
  if (isPublicPath(window.location.pathname)) return;
  if (import.meta.env.DEV) {
    // Dev SW handling is Vite/HMR's job; don't fight Workbox in dev.
    return;
  }
  // main.tsx calls this once, but a double-register would leak a second
  // Workbox instance and duplicate every lifecycle listener below.
  if (workbox) return;

  // injectManifest emits the compiled SW at /service-worker.js (the source
  // filename in vite.config.ts). Registering the wrong path 404s and silently
  // breaks the offline cache.
  const wb = new Workbox('/service-worker.js', { type: 'module' });
  workbox = wb;

  wb.addEventListener('installed', event => {
    if (!event.isUpdate) {
      // First install — nothing to interrupt; take control now.
      wb.messageSkipWaiting();
    }
  });

  // Fires when a new SW installs and does NOT auto-activate, and ALSO when one
  // was already waiting before register() was called
  // (`wasWaitingBeforeRegister`). That second case is what rescues a device
  // that has been sitting on an undelivered update from a previous session.
  wb.addEventListener('waiting', () => {
    useAppUpdate.getState().setUpdateReady(true);
  });

  wb.addEventListener('controlling', () => {
    if (!reloadOnControlling) return;
    // The new SW owns the page now, so the precache it serves matches the
    // chunk hashes in the new index.html. Reload to pick them up.
    window.location.reload();
  });

  void wb.register({ immediate: true });

  // ── Update discovery (see the file header) ────────────────────────────────
  const checkForUpdate = (): void => {
    // Offline, or the server is down: nothing to do, and an unhandled
    // rejection here would be noise on every dead-zone tick.
    void wb.update().catch(() => undefined);
  };

  window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
  // Coming back to the app, or back onto wifi, are the two moments a deploy is
  // most likely to have happened since we last looked.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  });
  window.addEventListener('online', checkForUpdate);
}

/**
 * How long to wait for `controlling` after posting SKIP_WAITING before
 * reloading anyway. See the fallback's comment below.
 */
const ACTIVATION_TIMEOUT_MS = 3_000;

/**
 * Posts SKIP_WAITING to the waiting SW and arms the reload that follows once it
 * takes control. Returns false if there is no registration (dev, or an
 * unsupported browser), in which case the caller's banner should not have been
 * on screen in the first place.
 */
export function triggerSkipWaitingReload(): boolean {
  if (!workbox) return false;
  reloadOnControlling = true;
  useAppUpdate.getState().setApplying(true);
  workbox.messageSkipWaiting();

  // messageSkipWaiting is a no-op when nothing is actually waiting, and that is
  // reachable: if the last other tab closed after the banner appeared, the
  // waiting SW activated on its own and `controlling` already fired (ignored,
  // because reloadOnControlling was still false). The banner would then sit on
  // "Updating…" forever. Reloading regardless is correct in both branches — the
  // outbox lives in IndexedDB and the banner never renders over a capture.
  window.setTimeout(() => {
    window.location.reload();
  }, ACTIVATION_TIMEOUT_MS);
  return true;
}
